import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AIPropertySuggestionsService, PropertySuggestion } from './ai-property-suggestions.service';
import { BuilderNode, BuilderComponentType } from '../interfaces/builder-screen.models';
import { BuilderScreenBetaService } from '../services/builder-screen.service';

@Component({
  selector: 'app-ai-suggestions-panel',
  templateUrl: './ai-suggestions-panel.component.html',
  styleUrls: ['./ai-suggestions-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AISuggestionsPanelComponent implements OnInit, OnDestroy {
  @Input() visible = false;
  @Input() componentType: BuilderComponentType | null = null;
  @Input() selectedNode: BuilderNode | null = null;
  
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() suggestionApplied = new EventEmitter<PropertySuggestion>();

  private readonly destroy$ = new Subject<void>();
  
  suggestions$: Observable<PropertySuggestion[]>;
  isLoading = false;

  constructor(
    private readonly aiSuggestionsService: AIPropertySuggestionsService,
    private readonly builderService: BuilderScreenBetaService,
    private readonly cdr: ChangeDetectorRef
  ) {
    // Inicializa com observable vazio, carregará sugestões quando componente for selecionado
    this.suggestions$ = of([]);
  }

  ngOnInit() {
    // Atualiza sugestões quando componente muda
    if (this.selectedNode) {
      this.loadSuggestions();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  show() {
    this.visible = true;
    this.visibleChange.emit(this.visible);
    
    // Carrega sugestões frescas quando painel abre
    if (this.selectedNode) {
      this.loadSuggestions();
    }
    
    this.cdr.markForCheck();
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.cdr.markForCheck();
  }

  private loadSuggestions() {
    if (!this.selectedNode) {
      this.suggestions$ = of([]);
      return;
    }
    
    this.isLoading = true;
    this.cdr.markForCheck();
    
    try {
      this.suggestions$ = this.aiSuggestionsService.getSuggestions(this.selectedNode, this.buildComponentContext());
    } catch (error) {
      console.warn('Error loading AI suggestions:', error);
      this.suggestions$ = of([]);
    }
    
    // Simula delay de carregamento para melhor UX
    setTimeout(() => {
      this.isLoading = false;
      this.cdr.markForCheck();
    }, 1000);
  }

  applySuggestion(suggestion: PropertySuggestion) {
    if (this.selectedNode) {
      // Aplica sugestão ao nó selecionado
      this.aiSuggestionsService.applySuggestion(suggestion, this.selectedNode);
      this.suggestionApplied.emit(suggestion);
      
      // Recarrega sugestões após aplicar uma
      this.loadSuggestions();
    }
  }

  dismissSuggestion(suggestion: PropertySuggestion, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    // Descarta sugestão
    this.aiSuggestionsService.dismissSuggestion(suggestion.id, 'not_interested');
    
          // Recarrega sugestões após descartar uma
    this.loadSuggestions();
  }

  refreshSuggestions() {
    if (this.selectedNode) {
      this.loadSuggestions();
    }
  }

  private buildComponentContext(): any {
    if (!this.selectedNode) return {};
    
    return {
      componentType: this.selectedNode.type,
      currentProperties: this.selectedNode.props || {},
      siblingComponents: [], // TODO: Obter do builder service
      parentComponent: undefined, // TODO: Obter do builder service
      userBehavior: {
        recentActions: [],
        commonPatterns: [],
        preferredStyles: {},
        errorHistory: []
      },
      formContext: {
        totalComponents: 5, // Valor simulado - TODO: Obter contagem real do builder service
        hasValidation: false,
        theme: 'light',
        purpose: 'form'
      }
    };
  }

  trackBySuggestionId(index: number, suggestion: PropertySuggestion): string {
    return suggestion.id;
  }

  getSuggestionIcon(suggestion: PropertySuggestion): string {
    switch (suggestion.category) {
      case 'performance': return 'rocket';
      case 'accessibility': return 'eye';
      case 'ux': return 'user';
      case 'improvement': return 'bg-colors';
      case 'validation': return 'safety';
      default: return 'bulb';
    }
  }

  getSuggestionColor(suggestion: PropertySuggestion): string {
    switch (suggestion.impact) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'blue';
      default: return 'default';
    }
  }
}
